/**
 * SEO utility functions for building structured data, OG image URLs,
 * and plain-text descriptions for meta tags.
 */

const SUPABASE_STORAGE_HOST = ".supabase.co/storage/";
/** Public-object path segment. Transform params are ignored here — see PUBLIC_RENDER_PATH. */
const PUBLIC_OBJECT_PATH = "/storage/v1/object/public/";
/** The only path that actually applies transform params. */
const PUBLIC_RENDER_PATH = "/storage/v1/render/image/public/";
/**
 * Raster formats the render endpoint can resize. MediaPicker accepts a bare `image/*`, so
 * an SVG cover is an ordinary upload — and the endpoint 400s on one, which would turn a
 * working hero into a broken image. Anything not listed stays on the plain object path.
 *
 * Deliberately excludes `gif`: transforming one flattens the animation, so leaving it
 * unoptimized is the better failure. Every live cover today is `.png` or `.jpeg`.
 */
const TRANSFORMABLE = /\.(png|jpe?g|webp|avif)$/i;

/** Default quality for a transform. Pass `quality` to override it for one surface. */
const QUALITY = 70;

interface SupabaseImageTransform {
  width?: number;
  height?: number;
  /**
   * `contain` preserves aspect ratio (what a responsive srcSet wants). `cover` crops to
   * fill a fixed frame (what a social card wants). Supabase's own default is a crop that
   * keeps the source height, so this is effectively required whenever `height` is omitted.
   */
  resize?: "cover" | "contain";
  /** 1–100. Defaults to `QUALITY`. */
  quality?: number;
}

/**
 * Rewrite a public Supabase Storage object URL to the `render/image` endpoint with
 * transform params, so the CDN returns a resized WebP instead of the raw upload.
 *
 * Returns the input unchanged when it isn't a public Supabase Storage object URL (a local
 * `/images/*` path, a third-party CDN, or a signed `/object/sign/` URL — rewriting a
 * token-bearing path would break the signature), or when it isn't a raster the endpoint
 * can resize. Idempotent: a URL already on the render path is left alone.
 *
 * `format=webp` is always explicit. The endpoint otherwise negotiates off `Accept` and
 * serves a full-quality PNG to any client that doesn't advertise WebP — 1.4 MB vs 99 KB
 * on the image that prompted this (#734).
 */
export function supabaseImageUrl(
  url: string | null | undefined,
  transform: SupabaseImageTransform = {},
): string | undefined {
  if (!url) return undefined;
  if (!url.includes(SUPABASE_STORAGE_HOST)) return url;
  // Also what makes this idempotent: a URL already on the render path doesn't carry the
  // object path either, so it falls out here untouched.
  if (!url.includes(PUBLIC_OBJECT_PATH)) return url;

  const [base, existingQuery] = url.split("?");
  if (!TRANSFORMABLE.test(base)) return url;

  const params = new URLSearchParams(existingQuery);
  if (transform.width) params.set("width", String(transform.width));
  if (transform.height) params.set("height", String(transform.height));
  if (transform.resize) params.set("resize", transform.resize);
  params.set("format", "webp");
  params.set("quality", String(transform.quality ?? QUALITY));

  return `${base.replace(PUBLIC_OBJECT_PATH, PUBLIC_RENDER_PATH)}?${params.toString()}`;
}

/**
 * One `supabaseImageUrl` variant per width, as `src` (the largest) plus a `srcSet`.
 *
 * Returns `null` when the URL isn't something the render endpoint can resize (a local
 * path, a third-party URL, a GIF or SVG), so the caller falls back to the plain URL.
 */
export function supabaseResponsiveImage(
  url: string | null | undefined,
  widths: readonly number[],
  transform: Omit<SupabaseImageTransform, "width"> = {},
): { src: string; srcSet: string } | null {
  if (widths.length === 0) return null;
  const variant = (width: number) => supabaseImageUrl(url, { ...transform, width });
  const largest = variant(widths[widths.length - 1]);
  if (!largest || largest === url) return null;
  return {
    src: largest,
    srcSet: widths.map((w) => `${variant(w)} ${w}w`).join(", "),
  };
}

/**
 * A Storage-hosted site logo as a WebP at 2x its tallest rendered height (80px). Returns
 * `undefined` for an empty URL so callers can fall back with `||`.
 */
export function logoImageUrl(url: string | null | undefined): string | undefined {
  return supabaseImageUrl(url, { height: 160, resize: "contain" });
}

/**
 * Social-card variant: 1200x630, cropped to fill. `cover` is deliberate — a social card
 * wants a fixed frame — and `height` is explicit, so no source-height crop applies.
 */
export function ogImageUrl(url: string | null | undefined): string | undefined {
  return supabaseImageUrl(url, { width: 1200, height: 630, resize: "cover" });
}

/**
 * Strip HTML and truncate to a max length, breaking at word boundaries.
 */
export function plainText(raw: string | null | undefined, maxLen = 160): string {
  if (!raw) return "";
  // Strip HTML tags (descriptions may contain rich text)
  const plain = raw.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  if (plain.length <= maxLen) return plain;
  const truncated = plain.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > maxLen * 0.6 ? truncated.slice(0, lastSpace) : truncated) + "...";
}

/** JSON-LD Product schema for a catering package. */
export function packageProductJsonLd(pkg: {
  name: string;
  description: string | null;
  price: number;
  cover_image_url: string | null;
  og_image_url?: string | null;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: pkg.name,
    description: plainText(pkg.description, 300),
    ...(pkg.og_image_url || pkg.cover_image_url
      ? { image: pkg.og_image_url || pkg.cover_image_url }
      : {}),
    brand: {
      "@type": "Organization",
      name: "Tristano's Catering",
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "CAD",
      price: Number(pkg.price).toFixed(2),
      availability: "https://schema.org/InStock",
    },
  };
}

/** JSON-LD Menu schema for a published menu. */
export function menuJsonLd(menu: {
  name: string;
  description: string | null;
  cover_image_url: string | null;
  og_image_url?: string | null;
  sections: { title: string; description: string | null; items: { menu_item: { name: string; description: string | null } | null }[] }[];
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Menu",
    name: menu.name,
    description: plainText(menu.description, 300),
    ...(menu.og_image_url || menu.cover_image_url
      ? { image: menu.og_image_url || menu.cover_image_url }
      : {}),
    hasMenuSection: menu.sections.map((section) => ({
      "@type": "MenuSection",
      name: section.title,
      ...(section.description ? { description: section.description } : {}),
      hasMenuItem: (section.items || [])
        .filter((item) => item.menu_item)
        .map((item) => ({
          "@type": "MenuItem",
          name: item.menu_item!.name,
          ...(item.menu_item!.description
            ? { description: item.menu_item!.description }
            : {}),
        })),
    })),
  };
}

/** JSON-LD ItemList for the packages browse page. */
export function packagesItemListJsonLd(
  packages: { name: string; slug: string }[],
  baseUrl: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Catering Packages",
    numberOfItems: packages.length,
    itemListElement: packages.map((pkg, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: pkg.name,
      url: `${baseUrl}/packages/${pkg.slug}`,
    })),
  };
}

/** JSON-LD FoodEstablishment for site-wide structured data. */
export function foodEstablishmentJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FoodEstablishment",
    name: "Tristano's Catering",
    url: "https://tristanoscatering.ca",
    // TODO: pull telephone + address from useAppSettings when available
    servesCuisine: "Italian, Mediterranean",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Toronto",
      addressRegion: "ON",
      addressCountry: "CA",
    },
  };
}
